import * as React from "react";

import * as DTO from "../../../../interfaces";
import { Page, VideoProject, VideoProjectServices } from "@/services/bedrock";

import { useHistory } from "react-router";

import { formatVideoProjectStatusDate, URLS, useAccountSettings } from "@/utils";
import { Button, Spinner } from "@/components/ui";
import { ChevronDown, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Svg } from "@/assets/svg";
import moment from "moment";
import { cn } from "@/lib/utils";
import LibraryCard from "@/components/Cells/LibraryCard";

interface OwnProps {
  player: DTO.PlayerResumeResponse;
}

type Props = OwnProps;

const PAGE_SIZE = 5;

const VideoProjectsList: React.FC<Props> = ({ player }) => {
  const history = useHistory();
  const [videoProjects, setVideoProjects] = React.useState<Page<VideoProject>>({
    content: [],
    pagination: { page: 1, perPage: PAGE_SIZE, totalPages: 0, totalCount: 0 },
  });
  const [loading, setLoading] = React.useState(false);
  const [display, setDisplay] = React.useState(true);
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(DTO.WebComponents.VIDEO_CATALYST);

  const loadPage = React.useCallback(
    async (page: number) => {
      setLoading(true);
      try {
        const response = await VideoProjectServices.index({
          playerId: player.playerId,
          page,
          perPage: PAGE_SIZE,
          sort: "created_date",
          order: "desc",
          extraFields: ["needsAck"],
        });
        setVideoProjects(response);
      } finally {
        setLoading(false);
      }
    },
    [player.playerId]
  );

  React.useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  const loadMore = React.useCallback(
    async (pageOffset: number) => {
      if (loading) return;
      const { page, totalPages } = videoProjects.pagination;
      const nextPage = page + pageOffset;
      if (nextPage > totalPages || nextPage < 1) return;
      await loadPage(nextPage);
    },
    [loading, loadPage, videoProjects.pagination]
  );

  const actionTitle = (): string => {
    const { page, perPage, totalCount } = videoProjects.pagination;
    if (!display) {
      return "Collapsed (" + totalCount + ")";
    }
    const start = totalCount === 0 ? 0 : (page - 1) * perPage;
    return `Showing ${start} - ${start + videoProjects.content.length} of ${totalCount}`;
  };

  const isFirstPage = videoProjects.pagination.page <= 1;
  const isLastPage = videoProjects.pagination.page >= videoProjects.pagination.totalPages;

  return (
    <div>
      <div
        className={cn(
          "flex items-center justify-between w-full p-2 border border-white text-foreground",
          display || "rounded-sm border-border bg-accent text-accent-foreground"
        )}
      >
        <div className="font-bold text-xl indent-1">{player.fullName}</div>
        <Button variant="ghost" size="xs" onClick={() => setDisplay(!display)}>
          {actionTitle()}
          {display ? <ChevronRight className="ml-2" /> : <ChevronDown className="ml-2" />}
        </Button>
      </div>
      {display && (
        <div className="flex gap-x-5 items-center flex-wrap">
          {!isFirstPage && (
            <Button
              variant="ghost"
              className="bg-accent"
              onClick={() => loadMore(-1)}
              disabled={loading}
              children={<ChevronLeft />}
            />
          )}

          {withWritePermission && (
            <Button
              variant="outline"
              className="bg-background hover:bg-background rounded-2 my-2 border border-border border-dashed w-38 h-65 hover:shadow-md flex flex-col items-center justify-center"
              onClick={() => {
                let locationValues = { playerId: player?.playerId };
                history.push(URLS.coach.newVideoProject + "?playerId=" + player?.playerId, locationValues);
              }}
            >
              <Plus className="border rounded-full border-border size-7 p-1" />
              Add new
            </Button>
          )}
          {loading && videoProjects.content.length === 0 ? (
            <Spinner className="size-12" />
          ) : (
            videoProjects.content.map(videoProject => {
              return (
                <LibraryCard
                  key={"video_project_card_" + videoProject.id}
                  title={videoProject.statusLabel}
                  thumbnail={videoProject.thumbnailUrl || Svg.PromptPlaceholder}
                  dateLabel={formatVideoProjectStatusDate(videoProject.statusDate)}
                  className={cn("h-65 min-h-65", videoProject.needsAck && "border-warning")}
                  disabled={loading}
                  onClick={() => history.push(URLS.coach.videoProject.replace(":id", `${videoProject.id}`))}
                />
              );
            })
          )}
          {!isLastPage && (
            <Button
              variant="ghost"
              className="bg-accent ml-auto"
              onClick={() => loadMore(1)}
              disabled={loading}
              children={<ChevronRight />}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default VideoProjectsList;
